export const GRID_COLUMNS = 2;

export const PHOTOS_PER_PAGE = 3;

export const ROSTER_PLAYER_KEY = 'grouped_roster_player_id';

export const PINNED_LOOK_NAME = 'Team';

export const hasGroupMetadata = (photo) => Boolean(photo) && ROSTER_PLAYER_KEY in photo;

export const rosterPlayerKey = (photo) => {
    const key = photo?.[ROSTER_PLAYER_KEY];

    return key === null || key === undefined || key === '' ? null : String(key);
};

export const isPinnedPhoto = (photo) => Boolean(photo?.pinned) || Boolean(photo?.pinned_at);

export const photoCaption = (photo) =>
    photo?.look_name ?? (isPinnedPhoto(photo) ? PINNED_LOOK_NAME : null);

const spansForPage = (count) => {
    if (count === 1) return [GRID_COLUMNS];
    if (count === 3) return [1, 1, GRID_COLUMNS];

    return Array(count).fill(1);
};

// The pinned/team photo always reads as the group shot, not one more photo
// of the player — so within its page it's pushed to the last (bottom, full
// width) slot rather than wherever it happened to fall in the source order.
const placePinnedLast = (pagePhotos) => {
    const pinnedIndex = pagePhotos.findIndex(isPinnedPhoto);

    if (pinnedIndex === -1 || pinnedIndex === pagePhotos.length - 1) {
        return pagePhotos;
    }

    const reordered = pagePhotos.filter((_, index) => index !== pinnedIndex);
    reordered.push(pagePhotos[pinnedIndex]);

    return reordered;
};

export const buildPhotoGridPages = (photos, perPage = PHOTOS_PER_PAGE) => {
    if (!Array.isArray(photos) || photos.length === 0) return [];

    const size = Math.max(1, perPage);
    const pages = [];

    for (let index = 0; index < photos.length; index += size) {
        const pagePhotos = placePinnedLast(photos.slice(index, index + size));
        const spans = spansForPage(pagePhotos.length);
        const lastPosition = pagePhotos.length - 1;

        pages.push(
            pagePhotos.map((photo, position) => ({
                photo,
                span: spans[position],
                caption: photoCaption(photo),
                // A full-width pinned tile at the bottom of a 3-photo page reads
                // as a short banner rather than a third full-size photo.
                banner: pagePhotos.length === 3 && position === lastPosition && isPinnedPhoto(photo),
            }))
        );
    }

    return pages;
};

export const isGroupedPreview = (photos) => Array.isArray(photos) && photos.length > 1;

export const entryBoundsAt = (photos, index) => {
    const key = rosterPlayerKey(photos?.[index]);

    if (key === null) {
        return { start: index, end: index };
    }

    let start = index;
    let end = index;

    while (start > 0 && rosterPlayerKey(photos[start - 1]) === key) {
        start--;
    }

    while (rosterPlayerKey(photos[end + 1]) === key) {
        end++;
    }

    return { start, end };
};

export const pinnedPhotosIn = (photos) => {
    if (!photos || typeof photos !== 'object') {
        return [];
    }

    return Object.values(photos).filter(isPinnedPhoto);
};

export const buildEntryGroup = (photos, index) => {
    if (rosterPlayerKey(photos?.[index]) === null) {
        return [];
    }

    const { start, end } = entryBoundsAt(photos, index);
    const seen = new Set();
    const group = [];

    const add = (photo) => {
        if (!photo?.id || seen.has(photo.id)) {
            return;
        }

        seen.add(photo.id);
        group.push(photo);
    };

    for (let position = start; position <= end; position++) {
        add(photos[position]);
    }

    pinnedPhotosIn(photos).forEach(add);

    return group;
};

// The grouped preview covers nearly the whole screen (nav buttons, pagination
// dots and the selection count take up the rest), so the grid can claim most
// of the viewport height instead of a conservative sliver of it.
export const GRID_AVAILABLE_VH = 60;

export const rowsForPage = (page) => {
    if (!Array.isArray(page) || page.length === 0) return 0;

    const columnsUsed = page.reduce((total, tile) => total + (tile?.span ?? 1), 0);

    return Math.ceil(columnsUsed / GRID_COLUMNS);
};

export const tileMaxHeightVh = (page, availableVh = GRID_AVAILABLE_VH) => {
    const rows = rowsForPage(page);

    if (rows === 0) return availableVh;

    return Math.floor(availableVh / rows);
};

// Fixed, deliberately short: the banner tile is a caption strip for the team
// photo, not a third full-size photo competing with the two above it.
export const BANNER_TILE_VH = 18;

/**
 * A banner tile (see buildPhotoGridPages) gets a small, fixed height; the
 * regular tiles sharing its page get whatever height that leaves, which is
 * more than the even split they'd otherwise get.
 */
export const tileHeightVh = (tile, page, availableVh = GRID_AVAILABLE_VH) => {
    const hasBanner = Array.isArray(page) && page.some((item) => item?.banner);

    if (!hasBanner) {
        return tileMaxHeightVh(page, availableVh);
    }

    const bannerHeight = Math.min(BANNER_TILE_VH, availableVh);

    return tile?.banner ? bannerHeight : Math.max(availableVh - bannerHeight, bannerHeight);
};
